import React from 'react';
import {
  Box,
  Flex,
  Button,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Badge,
  Text,
  Kbd,
  HStack,
  IconButton,
} from '@chakra-ui/react';
import {
  Search,
  Bell,
  Moon,
  Sun,
  User,
  Settings,
  ShieldCheck,
  LogOut,
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { useNavigate } from 'react-router-dom';

export const Header = ({ onOpenSearch, onOpenNotifications }) => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { notifications } = useNotification();
  const navigate = useNavigate();

  const unreadNotifications = notifications.filter((n) => !n.read).length;

  return (
    <Box
      as="header"
      position="sticky"
      top="0"
      bg="rgba(255, 255, 255, 0.85)"
      _dark={{ bg: 'rgba(17, 24, 39, 0.85)', borderColor: 'rgba(255, 255, 255, 0.08)' }}
      backdropFilter="blur(16px)"
      borderBottom="1px solid"
      borderColor="rgba(226, 232, 240, 0.8)"
      px={6}
      py={3}
      zIndex="90"
    >
      <Flex justify="space-between" align="center">
        {/* Global Search trigger button */}
        <Button
          onClick={onOpenSearch}
          variant="outline"
          borderRadius="16px"
          w={{ base: '180px', md: '320px' }}
          justifyContent="space-between"
          color="gray.500"
          bg="gray.50"
          _dark={{ bg: 'rgba(255,255,255,0.04)', borderColor: 'gray.700', color: 'gray.400' }}
          fontWeight="normal"
          size="sm"
          px={3.5}
        >
          <HStack spacing={2}>
            <Search size={16} />
            <Text fontSize="xs">Search users, events, IDs...</Text>
          </HStack>
          <HStack spacing={1} display={{ base: 'none', md: 'flex' }}>
            <Kbd fontSize="10px">Ctrl</Kbd>
            <Kbd fontSize="10px">K</Kbd>
          </HStack>
        </Button>

        {/* Right Header Actions */}
        <HStack spacing={3}>
          {/* Theme Toggle */}
          <IconButton
            size="sm"
            variant="ghost"
            borderRadius="12px"
            icon={isDark ? <Sun size={18} /> : <Moon size={18} />}
            onClick={toggleTheme}
            aria-label="Toggle Dark Mode"
          />

          {/* Notification Drawer Trigger */}
          <Box position="relative">
            <IconButton
              size="sm"
              variant="ghost"
              borderRadius="12px"
              icon={<Bell size={18} />}
              onClick={onOpenNotifications}
              aria-label="Open Notifications"
            />
            {unreadNotifications > 0 && (
              <Badge
                position="absolute"
                top="-2px"
                right="-2px"
                colorScheme="red"
                borderRadius="full"
                px={1.5}
                fontSize="10px"
              >
                {unreadNotifications}
              </Badge>
            )}
          </Box>

          {/* User Profile Menu */}
          <Menu placement="bottom-end">
            <MenuButton
              as={Button}
              variant="ghost"
              p={1}
              borderRadius="14px"
              _hover={{ bg: 'gray.100', _dark: { bg: 'rgba(255,255,255,0.05)' } }}
            >
              <HStack spacing={2}>
                <Avatar
                  size="xs"
                  name={user?.name || 'Super Admin'}
                  src={user?.avatar || user?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80'}
                />
                <Box textAlign="left" display={{ base: 'none', md: 'block' }}>
                  <Text fontSize="xs" fontWeight="bold" lineHeight="tight">
                    {user?.name || 'Super Admin'}
                  </Text>
                  <Text fontSize="10px" color="gray.500" lineHeight="tight">
                    {user?.role || 'Super Admin'}
                  </Text>
                </Box>
              </HStack>
            </MenuButton>
            <MenuList borderRadius="16px" p={2} _dark={{ bg: '#161F30', borderColor: 'gray.700' }}>
              <MenuItem borderRadius="10px" icon={<User size={16} />} onClick={() => navigate('/settings')}>
                My Profile
              </MenuItem>
              <MenuItem borderRadius="10px" icon={<Settings size={16} />} onClick={() => navigate('/settings')}>
                System Settings
              </MenuItem>
              <MenuItem borderRadius="10px" icon={<ShieldCheck size={16} />} onClick={() => navigate('/roles-permissions')}>
                Roles & Access
              </MenuItem>
              <MenuDivider />
              <MenuItem borderRadius="10px" icon={<LogOut size={16} />} color="red.500" onClick={logout}>
                Log Out
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>
    </Box>
  );
};
